"use client";

import React, { useEffect, useRef, useState } from "react";
import { Droplets, Leaf, ShieldCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const ecoFeatures = [{
  title: "01. Personlig service",
  description: "Som en dedikerad micro-byrå är vi aldrig mer än ett samtal bort. Vi lär känna din verksamhet på djupet för att ge rätt stöd.",
  icon: Droplets
}, {
  title: "02. Flexibilitet",
  description: "Vi anpassar våra digitala processer och tjänster efter dina unika behov, inte tvärtom. Skalbara lösningar för småföretagare.",
  icon: Leaf
}, {
  title: "03. Närhet",
  description: "Vi är din lokala partner i den digitala världen. Trygghet genom transparens, tillgänglighet och genuint engagemang i din framgång.",
  icon: ShieldCheck
}];
export default function OurEnvironmentalResponsibility() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="167854" id="varför-välja-logik" className="relative bg-background section overflow-hidden ![padding:40px_48px_40px_48px]">
      <div className="container mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          
          
          
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto">
          {ecoFeatures.map((feature, i) => {
          const Icon = feature.icon;
          return <div key={i} data-index={i} className={`group flex flex-col items-center text-center transition-all duration-700 ease-out delay-${i * 150} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <div className="mb-6 relative">
                  {}
                  <div className="absolute inset-0 bg-accent/5 rounded-full blur-xl group-hover:bg-accent/10 transition-colors duration-500"></div>
                  
                  {}
                  <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-white border border-border/10 group-hover:-translate-y-2 transition-transform duration-500 ease-out">
                    <Icon className="w-7 h-7 text-accent group-hover:scale-110 transition-transform duration-500 ease-out" />
                  </div>
                </div>
                
                <h3 className="h3 font-bold text-primary mb-3">
                  {feature.title}
                </h3>
                <p className="body text-muted-foreground">
                  {feature.description}
                </p>
              </div>;
        })}
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-0 pointer-events-none">
        <svg className="relative block w-full h-[60px] md:h-[120px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,119.3,192.39,101.5,236.4,89.15,279.4,71.5,321.39,56.44Z" className="fill-secondary/30"></path>
        </svg>
      </div>
    </section>;
}